import {
  DocumentNode,
  FetchResult,
  MutationFunctionOptions,
  MutationHookOptions,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import { useIntegratedClient } from '../useIntegratedClient';
import { AsyncState } from '../../constants/types';
import { ApolloClientKey } from '../../providers/ApolloClientsProvider';
import { useAbortController } from 'ui-components/src/hooks/useAbortController';
import { useErrorHandler } from '../useErrorHandler';

export function useIntegratedMutation<
  TData = any,
  TVariables = OperationVariables,
>(
  mutation: DocumentNode,
  clientKey: ApolloClientKey,
  options?: MutationHookOptions<TData, TVariables>,
): AsyncState<TData> & {
  performMutation: (
    options?: MutationFunctionOptions<TData, TVariables> | undefined,
  ) => Promise<FetchResult<TData, Record<string, any>, Record<string, any>>>;
} {
  const signal = useAbortController();
  const client = useIntegratedClient(clientKey);

  const [performMutation, result] = useMutation<TData, TVariables>(mutation, {
    client: client,
    errorPolicy: 'all',
    context: {
      fetchOptions: { signal },
    },
    ...options,
  });

  useErrorHandler(result);

  return {
    loading: result.loading,
    error: result.error,
    data: result.data || undefined,
    performMutation,
  };
}
